import React from 'react';
import { motion } from 'framer-motion';
import {
  SwatchIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const BrandAssetsPage: React.FC = () => {
  const colors = {
    primary: [
      { name: 'Primary 50', hex: '#fdf2f4', class: 'bg-pink-50' },
      { name: 'Primary 100', hex: '#fce8eb', class: 'bg-pink-100' },
      { name: 'Primary 300', hex: '#f9a8b8', class: 'bg-pink-300' },
      { name: 'Primary 500', hex: '#ec4899', class: 'bg-primary-500' },
      { name: 'Primary 600', hex: '#db2777', class: 'bg-pink-600' },
      { name: 'Primary 700', hex: '#be185d', class: 'bg-pink-700' },
    ],
    dark: [
      { name: 'Dark 700', hex: '#374151', class: 'bg-dark-700' },
      { name: 'Dark 800', hex: '#1f2937', class: 'bg-dark-800' },
      { name: 'Dark 900', hex: '#111827', class: 'bg-dark-900' },
      { name: 'Dark 950', hex: '#030712', class: 'bg-dark-950' },
    ],
    accent: [
      { name: 'Success', hex: '#10b981', class: 'bg-green-500' },
      { name: 'Warning', hex: '#f59e0b', class: 'bg-yellow-500' },
      { name: 'Error', hex: '#ef4444', class: 'bg-red-500' },
      { name: 'Info', hex: '#3b82f6', class: 'bg-blue-500' },
    ]
  };

  const logos = [
    { name: 'Primary Logo', description: 'Full color on dark', formats: ['SVG', 'PNG'], size: '2.4 MB' },
    { name: 'White Logo', description: 'White version for dark backgrounds', formats: ['SVG', 'PNG'], size: '2.1 MB' },
    { name: 'Black Logo', description: 'Black version for light backgrounds', formats: ['SVG', 'PNG'], size: '2.1 MB' },
    { name: 'Icon Only', description: 'Logomark without text', formats: ['SVG', 'PNG', 'ICO'], size: '1.5 MB' },
    { name: 'Wordmark', description: 'Text-only logo', formats: ['SVG', 'PNG'], size: '1.8 MB' },
    { name: 'Social Avatar', description: 'Square format for social media', formats: ['PNG', 'JPG'], size: '0.5 MB' },
  ];

  const dosAndDonts = {
    dos: [
      'Use the logo on solid backgrounds',
      'Maintain minimum clear space',
      'Use approved color variations',
      'Scale proportionally',
      'Use high-resolution assets',
    ],
    donts: [
      'Stretch or distort the logo',
      'Change the colors',
      'Add effects (shadows, gradients)',
      'Place on busy backgrounds',
      'Recreate or modify the logo',
      'Use the logo at small sizes below 24px',
    ]
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-dark-900 to-dark-950">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <SwatchIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Brand Assets
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Official logos, colors, and guidelines for TEMPLUM branding
            </p>
            <a
              href="/brand-assets.zip"
              download
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Download Complete Brand Kit
            </a>
          </motion.div>
        </div>
      </section>

      {/* Logo Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Logos</h2>

            {/* Primary Logo Display */}
            <div className="bg-dark-800/50 rounded-2xl p-12 border border-dark-700 mb-8 flex items-center justify-center">
              <div className="text-center">
                <div className="w-64 h-24 bg-gradient-to-r from-primary-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-4xl font-bold text-white">TEMPLUM</span>
                </div>
                <p className="text-gray-400 text-sm">Primary Logo</p>
              </div>
            </div>

            {/* Logo Downloads */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {logos.map((logo, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                >
                  <div className="h-20 bg-dark-900 rounded-lg mb-4 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-400">T</span>
                  </div>
                  <h3 className="text-white font-semibold mb-1">{logo.name}</h3>
                  <p className="text-gray-400 text-sm mb-3">{logo.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {logo.formats.map((format, fidx) => (
                        <span key={fidx} className="px-2 py-0.5 bg-dark-700 text-gray-400 text-xs rounded">
                          {format}
                        </span>
                      ))}
                    </div>
                    <button className="p-2 hover:bg-dark-700 rounded-lg transition-colors">
                      <ArrowDownTrayIcon className="w-5 h-5 text-primary-400" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Color Palette */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Color Palette</h2>

            {/* Primary Colors */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Primary</h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                {colors.primary.map((color, idx) => (
                  <div key={idx} className="text-center">
                    <div
                      className={`w-full h-20 rounded-lg ${color.class} mb-2`}
                      style={{ backgroundColor: color.hex }}
                    />
                    <p className="text-white text-sm">{color.name}</p>
                    <p className="text-gray-400 text-xs font-mono">{color.hex}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Dark Colors */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Dark Theme</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {colors.dark.map((color, idx) => (
                  <div key={idx} className="text-center">
                    <div
                      className={`w-full h-20 rounded-lg border border-dark-600 mb-2`}
                      style={{ backgroundColor: color.hex }}
                    />
                    <p className="text-white text-sm">{color.name}</p>
                    <p className="text-gray-400 text-xs font-mono">{color.hex}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Accent Colors */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Accent Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {colors.accent.map((color, idx) => (
                  <div key={idx} className="text-center">
                    <div
                      className={`w-full h-20 rounded-lg ${color.class} mb-2`}
                    />
                    <p className="text-white text-sm">{color.name}</p>
                    <p className="text-gray-400 text-xs font-mono">{color.hex}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Typography */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Typography</h2>
            <div className="bg-dark-800/50 rounded-xl p-8 border border-dark-700">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Primary Font</h3>
                  <p className="text-4xl font-bold text-white mb-2">Inter</p>
                  <p className="text-gray-400 mb-4">
                    Used for all headings and body text throughout the platform.
                  </p>
                  <div className="space-y-2">
                    <p className="font-light text-gray-300">Light (300)</p>
                    <p className="font-normal text-gray-300">Regular (400)</p>
                    <p className="font-medium text-gray-300">Medium (500)</p>
                    <p className="font-semibold text-gray-300">Semibold (600)</p>
                    <p className="font-bold text-gray-300">Bold (700)</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Monospace Font</h3>
                  <p className="text-4xl font-bold text-white font-mono mb-2">JetBrains Mono</p>
                  <p className="text-gray-400 mb-4">
                    Used for code snippets, addresses, and technical content.
                  </p>
                  <div className="p-4 bg-dark-900 rounded-lg font-mono">
                    <p className="text-primary-400">0x1234...5678</p>
                    <p className="text-gray-400">const auction = await...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Do's and Don'ts */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Logo Usage Guidelines</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Do's */}
              <div className="bg-dark-800/50 rounded-xl p-6 border border-green-500/30">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckIcon className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-green-400">Do</h3>
                </div>
                <ul className="space-y-3">
                  {dosAndDonts.dos.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <CheckIcon className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Don'ts */}
              <div className="bg-dark-800/50 rounded-xl p-6 border border-red-500/30">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                    <XMarkIcon className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-red-400">Don't</h3>
                </div>
                <ul className="space-y-3">
                  {dosAndDonts.donts.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300">
                      <XMarkIcon className="w-4 h-4 text-red-400 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clear Space */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Clear Space</h2>
            <div className="bg-dark-800/50 rounded-xl p-8 border border-dark-700">
              <div className="flex justify-center mb-6">
                <div className="relative p-12 border-2 border-dashed border-primary-500/50 rounded-xl">
                  <div className="w-32 h-12 bg-gradient-to-r from-primary-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-xl font-bold text-white">TEMPLUM</span>
                  </div>
                  <div className="absolute top-0 left-12 w-8 h-full border-l border-dashed border-gray-500" />
                  <div className="absolute top-0 right-12 w-8 h-full border-r border-dashed border-gray-500" />
                  <div className="absolute left-0 top-8 h-8 w-full border-t border-dashed border-gray-500" />
                  <div className="absolute left-0 bottom-8 h-8 w-full border-b border-dashed border-gray-500" />
                </div>
              </div>
              <p className="text-gray-400 text-center">
                Maintain a minimum clear space equal to the height of the "T" letterform around the logo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Guidelines Download */}
      <section className="py-16 bg-gradient-to-r from-primary-500/20 to-purple-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <DocumentTextIcon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              Complete Brand Guidelines
            </h2>
            <p className="text-gray-300 mb-8">
              Download our comprehensive brand guidelines PDF for detailed usage instructions.
            </p>
            <a
              href="/TEMPLUM_Brand_Guidelines.pdf"
              download
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Download Brand Guidelines (PDF)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandAssetsPage;
